import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
// Iter-220q — GeocodeCoverageChip relocated from /admin/home → Operations
// Center Data Maintenance tab. Background system metric does not belong in
// primary dashboard space; admins doing data work can find it under Ops.
import GeocodeCoverageChip from '../../components/GeocodeCoverageChip';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { formatTime12h, formatTimeRange } from '../../utils/timeFormat';
import {
  ArrowLeft, AlertTriangle, Clock, CheckCircle, UserX, Copy,
  Timer, ClipboardList, Zap, RefreshCw, Loader2, Shield, Search,
  CalendarDays, MapPin, Users, DollarSign, ChevronRight, ChevronDown, ChevronUp, Coffee,
  Filter, X, Building2, Sparkles, Hourglass, LogIn, LogOut, Database,
  ExternalLink, Camera, Edit3, ShieldAlert, FileText, User,
  Receipt, XCircle,  // Iter-232c
  FileCheck, MessageSquareQuote, BadgeCheck,  // Iter-246c — Daily Workflows
  Radio, AlertCircle,  // Iter-248 — Live Command Center + IDV Expiration tiles
  Cake, Award, Star, Trophy,  // Iter-249 — Today's Celebrations
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
// Iter-372 (Frontend) — inline timesheet clock-in/out corrections
import TimesheetEditModal from '../../components/TimesheetEditModal';
import ClockCorrectionControls from '../../components/ClockCorrectionControls';
import AdminStaffPaymentsLifecycle from '../../components/AdminStaffPaymentsLifecycle';

const API = process.env.REACT_APP_BACKEND_URL;

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

// ─── Quick Action Card ─────────────────────────────────────
function QuickActionCard({ icon: Icon, label, count, color, onClick, testId }) {
  const colorMap = {
    red: 'text-red-500 bg-red-500/10 border-red-500/20',
    amber: 'text-amber-500 bg-amber-500/10 border-amber-500/20',
    cyan: 'text-[#03DFFC] bg-[#03DFFC]/10 border-[#03DFFC]/20',
    green: 'text-green-600 bg-green-500/10 border-green-500/20',
    purple: 'text-purple-600 bg-purple-500/10 border-purple-500/20',  // Iter-232c
  };
  return (
    <Card
      data-testid={testId}
      className={`bg-gray-50 border ${count > 0 ? colorMap[color] : 'border-gray-200'} cursor-pointer hover:bg-gray-750 transition-all group`}
      onClick={onClick}
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${count > 0 ? colorMap[color] : 'bg-gray-100/50 text-gray-500'}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{count}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-gray-500 transition-colors" />
      </CardContent>
    </Card>
  );
}

// ─── No-Show Panel ─────────────────────────────────────────
function NoShowPanel() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/operations/no-show-check-needed`, { headers: getHeaders() });
      const data = await res.json();
      setShifts(data.shifts_needing_check || []);
    } catch { toast.error('Failed to load no-show data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  const detectNoShows = async (shiftId) => {
    setProcessing(p => ({ ...p, [shiftId]: true }));
    try {
      const res = await fetch(`${API}/api/operations/detect-no-shows/${shiftId}?grace_minutes=15`, {
        method: 'POST', headers: getHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'No-show detection complete');
        fetchShifts();
      } else {
        toast.info(data.message || data.detail || 'Check again later');
      }
    } catch { toast.error('Detection failed'); }
    finally { setProcessing(p => ({ ...p, [shiftId]: false })); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#03DFFC]" /></div>;

  return (
    <div data-testid="noshow-panel" className="space-y-3">
      {shifts.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <UserX className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No shifts currently need no-show checking</p>
        </div>
      ) : shifts.map(s => (
        <Card key={s.shift_id} className="bg-gray-50/60 border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-sm font-medium text-gray-900">{s.venue_name}</span>
                  <Badge className={`text-xs ${s.urgency === 'high' ? 'bg-red-500/20 text-red-500' : s.urgency === 'medium' ? 'bg-amber-500/20 text-amber-500' : 'bg-[#03DFFC]/20 text-[#03DFFC]'}`}>
                    {s.urgency}
                  </Badge>
                </div>
                <div className="flex gap-4 text-xs text-gray-500">
                  <span>{s.role_name} — {s.start_time}</span>
                  <span>{s.minutes_past_start}m past start</span>
                  <span>{s.pending_checkins} pending / {s.checked_in} checked in</span>
                </div>
              </div>
              <Button
                data-testid={`detect-noshow-${s.shift_id}`}
                size="sm"
                variant="destructive"
                disabled={processing[s.shift_id]}
                onClick={() => detectNoShows(s.shift_id)}
                className="bg-red-600 hover:bg-red-700"
              >
                {processing[s.shift_id] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Detect No-Shows'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Pending Timesheets Panel ──────────────────────────────
// Sprint 1B (Iter-220l) — Operations Center UX overhaul
//   • Bulk approve/reject already existed; filters + clear status + break
//     deductions + mobile cards layered on top.
//   • Prominent Talent / Event / Client columns.
//   • Replaces "IN/OUT" labels with "Clock In / Clock Out".
//   • Net-hours and break-minutes totals are surfaced in the summary bar.
const STATUS_BADGES = {
  completed: { label: 'Awaiting Approval', cls: 'bg-amber-100 text-amber-800 border border-amber-200' },
  checked_in: { label: 'Clocked Out — Pending', cls: 'bg-amber-100 text-amber-800 border border-amber-200' },
  timesheet_approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
  timesheet_rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700 border border-red-200' },
  paid: { label: 'Paid', cls: 'bg-cyan-100 text-cyan-800 border border-cyan-200' },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGES[status] || { label: status || '—', cls: 'bg-gray-100 text-gray-700 border border-gray-200' };
  return <Badge data-testid={`timesheet-status-${status}`} className={`text-[10px] uppercase tracking-wider ${s.cls}`}>{s.label}</Badge>;
}

function todayIso(daysOffset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + daysOffset);
  return d.toISOString().slice(0, 10);
}

// Sprint 1B — Days the timesheet has been awaiting approval.
// Uses shift_date (YYYY-MM-DD) — the longer the shift sat, the more urgent.
function daysAwaiting(shiftDateIso) {
  if (!shiftDateIso) return 0;
  try {
    const shift = new Date(shiftDateIso + 'T00:00:00Z');
    const now = new Date();
    return Math.max(0, Math.floor((now - shift) / 86_400_000));
  } catch { return 0; }
}

// Sprint 1B — Left-border urgency tint:
//   >7d  → red    (overdue)
//   >3d  → amber  (stale)
//   ≤3d  → cyan   (fresh)
// Returned as inline style so shadcn's tailwind-merge cn() cannot strip the
// border-l color (it collapses border-l-{color} when paired with border-{color}).
function urgencyBorderStyle(shiftDateIso) {
  const d = daysAwaiting(shiftDateIso);
  let color = '#22d3ee'; // cyan-400 — fresh
  if (d > 7) color = '#ef4444'; // red-500 — overdue
  else if (d > 3) color = '#f59e0b'; // amber-500 — stale
  return { borderLeftColor: color, borderLeftWidth: '4px', borderLeftStyle: 'solid' };
}

// Sprint 1B — Friendly date-group label for the mobile sticky header.
function dateGroupLabel(iso) {
  if (!iso) return 'Undated';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return iso; }
}

// Sprint 1B — Mobile 15-minute break stepper (replaces cramped numeric input
// on small screens). Renders ▲▼ chevrons + display, each tap adds/subtracts 15.
function MobileBreakStepper({ value, onChange, onCommit, saving, testIdPrefix }) {
  const step = 15;
  const minutes = Math.max(0, Math.min(480, Number(value) || 0));
  const dec = () => { const next = Math.max(0, minutes - step); onChange(String(next)); onCommit(next); };
  const inc = () => { const next = Math.min(480, minutes + step); onChange(String(next)); onCommit(next); };
  return (
    <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
      <Coffee className="w-4 h-4 text-amber-600 shrink-0" />
      <Label className="text-[11px] text-amber-800 flex-1">Unpaid Break</Label>
      <button
        type="button"
        onClick={dec}
        disabled={minutes <= 0 || saving}
        data-testid={`${testIdPrefix}-dec`}
        className="min-w-11 min-h-11 px-3 rounded-md bg-white border border-amber-300 text-amber-700 font-bold disabled:opacity-40 active:bg-amber-100"
        aria-label="Decrease break by 15 minutes"
      >−</button>
      <div
        className="min-w-[60px] text-center px-2 py-1.5 rounded-md bg-white border border-amber-200"
        data-testid={`${testIdPrefix}-value`}
      >
        <span className="text-sm font-semibold text-amber-900">{minutes}</span>
        <span className="text-[10px] text-amber-700 ml-0.5">min</span>
      </div>
      <button
        type="button"
        onClick={inc}
        disabled={minutes >= 480 || saving}
        data-testid={`${testIdPrefix}-inc`}
        className="min-w-11 min-h-11 px-3 rounded-md bg-white border border-amber-300 text-amber-700 font-bold disabled:opacity-40 active:bg-amber-100"
        aria-label="Increase break by 15 minutes"
      >+</button>
      {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-600" />}
    </div>
  );
}

// ─── Iter-226d — Approval-context flag chips + detail drawer ──
const FLAG_META = {
  overtime:         { label: 'OT',         cls: 'bg-purple-100 text-purple-700 border border-purple-200', title: 'Overtime hours' },
  late:             { label: 'Late',       cls: 'bg-red-100 text-red-700 border border-red-200',         title: 'Late clock-in' },
  early:            { label: 'Early',      cls: 'bg-blue-100 text-blue-700 border border-blue-200',      title: 'Early clock-in (>5 min)' },
  edited:           { label: 'Edited',     cls: 'bg-amber-100 text-amber-800 border border-amber-200',   title: 'Clock times manually edited' },
  manager_override: { label: 'Override',   cls: 'bg-amber-100 text-amber-800 border border-amber-200',   title: 'Manager override applied' },
  gps_failed:       { label: 'No GPS',     cls: 'bg-rose-100 text-rose-700 border border-rose-200',       title: 'GPS verification failed/missing' },
  selfie_failed:    { label: 'No Selfie',  cls: 'bg-rose-100 text-rose-700 border border-rose-200',       title: 'Selfie verification failed/missing' },
  missing_clockout: { label: 'No CO',      cls: 'bg-orange-100 text-orange-700 border border-orange-200', title: 'Missing clock-out' },
  needs_review:     { label: 'Review',     cls: 'bg-yellow-100 text-yellow-800 border border-yellow-200', title: 'Clock-in needs review' },
  pending_reimbursements: { label: 'Reimb', cls: 'bg-purple-100 text-purple-700 border border-purple-200', title: 'Reimbursements pending review' },
};

function FlagChips({ flags, compact = false }) {
  if (!flags) return null;
  const active = Object.entries(flags).filter(([, v]) => v);
  if (active.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${compact ? '' : 'mt-1'}`} data-testid="flag-chips">
      {active.map(([k]) => {
        const m = FLAG_META[k] || { label: k, cls: 'bg-gray-100 text-gray-700' };
        return (
          <span
            key={k}
            title={m.title}
            data-testid={`flag-chip-${k}`}
            className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-md font-semibold ${m.cls}`}
          >
            {m.label}
          </span>
        );
      })}
    </div>
  );
}

// Bulk-approve confirmation dialog showing total counts + amounts + flag warnings
function BulkConfirmDialog({ open, onClose, onConfirm, action, selectedTimesheets, isApproving }) {
  if (!open || !selectedTimesheets || selectedTimesheets.length === 0) return null;
  const totals = selectedTimesheets.reduce(
    (acc, t) => {
      const raw = Number(t.raw_hours || t.hours_worked || 0);
      const brk = Number(t.break_minutes || 0);
      const net = Math.max(0, raw - brk / 60);
      acc.net += net;
      acc.amount += net * (t.pay_rate || 0);
      acc.ot += Number(t.overtime_hours || 0);
      const f = t.flags || {};
      if (f.overtime) acc.ot_count += 1;
      if (f.edited) acc.edited += 1;
      if (f.manager_override) acc.overridden += 1;
      if (f.gps_failed) acc.gps += 1;
      if (f.selfie_failed) acc.selfie += 1;
      if (f.late) acc.late += 1;
      // Iter-232c — pending reimbursements awareness
      acc.reimb_pending_count += Number(t.expense_summary?.pending_count || 0);
      acc.reimb_pending_total += Number(t.expense_summary?.pending_total || 0);
      acc.reimb_approved_total += Number(t.expense_summary?.approved_total || 0);
      return acc;
    },
    { net: 0, amount: 0, ot: 0, ot_count: 0, edited: 0, overridden: 0, gps: 0, selfie: 0, late: 0,
      reimb_pending_count: 0, reimb_pending_total: 0, reimb_approved_total: 0 },
  );
  const warningCount = totals.ot_count + totals.edited + totals.overridden + totals.gps + totals.selfie;
  const isApprove = action === 'approve';
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="bulk-confirm-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isApprove ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <X className="w-5 h-5 text-red-600" />}
            {isApprove ? 'Confirm bulk approval' : 'Confirm bulk rejection'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-gray-700" data-testid="bulk-confirm-summary">
            You are {isApprove ? 'approving' : 'rejecting'}{' '}
            <span className="font-semibold">{selectedTimesheets.length} timesheets</span> totaling{' '}
            <span className="font-semibold">{totals.net.toFixed(1)} hours</span> and{' '}
            <span className="font-semibold text-emerald-700">
              ${totals.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>.
          </p>

          {/* Iter-232c — Settlement breakdown + reimbursement awareness */}
          {(totals.reimb_pending_count > 0 || totals.reimb_approved_total > 0) && (
            <div className="rounded-md border border-purple-200 bg-purple-50 p-3 space-y-1" data-testid="bulk-confirm-reimbursements">
              <p className="text-xs font-semibold text-purple-900 flex items-center gap-1">
                <Receipt className="w-3.5 h-3.5" />
                Reimbursements on these bookings
              </p>
              <ul className="text-[12px] text-purple-800 space-y-0.5 pl-5 list-disc">
                {totals.reimb_pending_count > 0 && (
                  <li>
                    <strong>{totals.reimb_pending_count}</strong> still <em>Under Review</em>
                    {' '}(${totals.reimb_pending_total.toFixed(2)}) — approving timesheets does <strong>NOT</strong> auto-approve these. Review them in the row drawer below.
                  </li>
                )}
                {totals.reimb_approved_total > 0 && (
                  <li>
                    ${totals.reimb_approved_total.toFixed(2)} already approved — will be added to talent settlement.
                  </li>
                )}
              </ul>
              <p className="text-[11px] text-purple-900 pt-1 border-t border-purple-200">
                Settlement total: Hourly <strong>${totals.amount.toFixed(2)}</strong> + Reimbursements <strong>${totals.reimb_approved_total.toFixed(2)}</strong> = <strong>${(totals.amount + totals.reimb_approved_total).toFixed(2)}</strong>
              </p>
            </div>
          )}
          {warningCount > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-1" data-testid="bulk-confirm-warnings">
              <p className="text-xs font-semibold text-amber-900 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                {warningCount} timesheet{warningCount === 1 ? '' : 's'} need extra attention:
              </p>
              <ul className="text-[12px] text-amber-800 space-y-0.5 pl-5 list-disc">
                {totals.ot_count > 0 && <li>{totals.ot_count} with overtime ({totals.ot.toFixed(1)}h total OT)</li>}
                {totals.edited > 0 && <li>{totals.edited} with manually edited clock times</li>}
                {totals.overridden > 0 && <li>{totals.overridden} with manager override applied</li>}
                {totals.gps > 0 && <li>{totals.gps} with missing/failed GPS verification</li>}
                {totals.selfie > 0 && <li>{totals.selfie} with missing/failed selfie verification</li>}
                {totals.late > 0 && <li>{totals.late} late clock-in</li>}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={isApproving} data-testid="bulk-confirm-cancel">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isApproving}
            className={isApprove ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
            data-testid="bulk-confirm-go"
          >
            {isApproving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isApprove ? `Approve ${selectedTimesheets.length}` : `Reject ${selectedTimesheets.length}`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Expanded per-row drawer — scheduled vs actual, GPS/selfie, edit history, quick actions
function TimesheetDetailDrawer({ row, onNavigate, onChange, onEditTimes }) {
  const [busyId, setBusyId] = React.useState(null);
  const [pendingNotes, setPendingNotes] = React.useState({});

  const reviewExpense = async (expense, action, adjustedAmount) => {
    setBusyId(expense.id);
    try {
      const body = { action, notes: pendingNotes[expense.id] || '' };
      if (action === 'approve' && adjustedAmount != null && adjustedAmount !== '' && Number(adjustedAmount) !== expense.amount) {
        body.adjusted_amount = Number(adjustedAmount);
      }
      await api.patch(`/admin/expenses/${expense.id}`, body);
      toast.success(`Reimbursement ${action}d`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setBusyId(null);
    }
  };

  if (!row) return null;
  const navItem = (icon, label, onClick, testId) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex items-center gap-1.5 text-xs font-medium text-cyan-700 hover:text-cyan-900 hover:bg-cyan-50 px-2 py-1 rounded-md transition-colors"
    >
      {icon} {label} <ExternalLink className="w-3 h-3" />
    </button>
  );
  return (
    <div data-testid={`detail-drawer-${row.booking_id}`} className="col-span-full bg-gray-50/60 border-t border-gray-200 px-4 py-3 rounded-b-lg">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
        {/* Scheduled vs actual */}
        <div className="bg-white rounded-md p-3 border border-gray-100">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1">
            <Clock className="w-3 h-3 text-cyan-600" /> Scheduled vs Actual
          </p>
          <div className="mt-2 space-y-1 text-gray-700">
            <div className="flex justify-between"><span className="text-gray-500">Scheduled:</span><span className="font-medium">{row.scheduled_start || '—'} → {row.scheduled_end || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Actual:</span><span className="font-medium">{row.clock_in || '—'} → {row.clock_out || '—'}</span></div>
            {row.original_clock_in && (
              <div className="flex justify-between text-amber-700"><span>Original CI:</span><span className="line-through">{row.original_clock_in}</span></div>
            )}
            {row.original_clock_out && (
              <div className="flex justify-between text-amber-700"><span>Original CO:</span><span className="line-through">{row.original_clock_out}</span></div>
            )}
            {row.late_minutes > 0 && (
              <div className="flex justify-between text-red-600"><span>Late by:</span><span className="font-medium">{row.late_minutes} min</span></div>
            )}
            {row.overtime_hours > 0 && (
              <div className="flex justify-between text-purple-700"><span>Overtime:</span><span className="font-medium">{row.overtime_hours.toFixed(2)} h</span></div>
            )}
            {onEditTimes && (
              <button
                type="button"
                onClick={() => onEditTimes(row)}
                data-testid={`edit-times-btn-${row.booking_id}`}
                className="mt-2 w-full flex items-center justify-center gap-1 text-[11px] font-medium text-cyan-700 hover:text-cyan-900 bg-cyan-50 hover:bg-cyan-100 border border-cyan-200 rounded-md px-2 py-1.5 transition-colors"
              >
                <Edit3 className="w-3 h-3" /> Edit clock times
              </button>
            )}
            {/* Undo an accidental early clock-out → talent goes back to Clocked In */}
            <ClockCorrectionControls
              compact
              showEdit={false}
              booking={{
                booking_id: row.booking_id,
                clock_out_time: row.clock_out_iso || row.clock_out,
                clock_in_time: row.clock_in_iso || row.clock_in,
                booking_status: row.status || row.booking_status,
                hours_approved: row.hours_approved,
                talent_name: row.talent_name,
              }}
              onDone={onChange}
            />
          </div>
        </div>

        {/* Verification */}
        <div className="bg-white rounded-md p-3 border border-gray-100">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1">
            <Shield className="w-3 h-3 text-cyan-600" /> Verification
          </p>
          <div className="mt-2 space-y-1.5 text-gray-700">
            <div className="flex items-center gap-2">
              <MapPin className="w-3 h-3 text-emerald-500" />
              <span className="text-gray-500">GPS in:</span>
              <span className="font-mono text-[11px]">
                {row.clock_in_lat != null ? `${row.clock_in_lat.toFixed(4)}, ${row.clock_in_lng.toFixed(4)}` : <em className="text-rose-600">missing</em>}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-3 h-3 text-red-500" />
              <span className="text-gray-500">GPS out:</span>
              <span className="font-mono text-[11px]">
                {row.clock_out_lat != null ? `${row.clock_out_lat.toFixed(4)}, ${row.clock_out_lng.toFixed(4)}` : <em className="text-rose-600">missing</em>}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Camera className="w-3 h-3 text-emerald-500" />
              <span className="text-gray-500">Selfie in:</span>
              {row.check_in_selfie_url
                ? <a href={row.check_in_selfie_url} target="_blank" rel="noreferrer" className="text-cyan-700 underline">view</a>
                : <em className="text-rose-600">missing</em>}
            </div>
            <div className="flex items-center gap-2">
              <Camera className="w-3 h-3 text-red-500" />
              <span className="text-gray-500">Selfie out:</span>
              {row.check_out_selfie_url
                ? <a href={row.check_out_selfie_url} target="_blank" rel="noreferrer" className="text-cyan-700 underline">view</a>
                : <em className="text-rose-600">missing</em>}
            </div>
            {row.clock_in_fallback_reason && (
              <p className="text-amber-700 text-[11px] italic">Fallback: {row.clock_in_fallback_reason}</p>
            )}
          </div>
        </div>

        {/* Override + notes + quick actions */}
        <div className="bg-white rounded-md p-3 border border-gray-100">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1">
            <FileText className="w-3 h-3 text-cyan-600" /> Notes & Actions
          </p>
          <div className="mt-2 space-y-2 text-gray-700">
            {row.checkin_override_reason && (
              <div className="text-amber-700 text-[11px]">
                <span className="font-semibold">CI override:</span> {row.checkin_override_reason}
              </div>
            )}
            {row.checkout_override_reason && (
              <div className="text-amber-700 text-[11px]">
                <span className="font-semibold">CO override:</span> {row.checkout_override_reason}
              </div>
            )}
            {row.timesheet_notes && (
              <div className="text-[11px] text-gray-700"><span className="font-semibold">Note:</span> {row.timesheet_notes}</div>
            )}
            <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-100 mt-1">
              {navItem(<ClipboardList className="w-3 h-3" />, 'Open Shift', () => onNavigate(`/admin/shifts/${row.shift_id}`), `open-shift-${row.booking_id}`)}
              {row.shift_id && navItem(<CalendarDays className="w-3 h-3" />, 'View Calendar', () => onNavigate(`/admin/operations/event/${row.shift_id}`), `view-calendar-${row.booking_id}`)}
              {row.shift_id && navItem(<FileText className="w-3 h-3" />, 'View Recap', () => onNavigate(`/admin/shifts/${row.shift_id}/recap-v2`), `view-recap-${row.booking_id}`)}
              {row.talent_id && navItem(<User className="w-3 h-3" />, 'Talent Profile', () => onNavigate(`/admin/talent/${row.talent_id}`), `talent-profile-${row.booking_id}`)}
            </div>
          </div>
        </div>
      </div>

      {/* ── Iter-232c — Reimbursements review (inline with timesheet approval) ── */}
      {row.expenses && row.expenses.length > 0 && (
        <div className="mt-3 bg-white rounded-md p-3 border border-purple-100" data-testid={`reimb-panel-${row.booking_id}`}>
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1">
              <Receipt className="w-3 h-3 text-purple-600" />
              Reimbursements
              <span className="ml-1 text-[10px] normal-case font-medium text-purple-700">
                {row.expense_summary?.pending_count || 0} pending ·
                ${Number(row.expense_summary?.pending_total || 0).toFixed(2)}
                {(row.expense_summary?.approved_total || 0) > 0 && (
                  <span className="ml-1 text-emerald-700">
                    · ${Number(row.expense_summary.approved_total).toFixed(2)} approved
                  </span>
                )}
              </span>
            </p>
            <button
              type="button"
              onClick={() => onNavigate(`/admin/expenses?booking=${row.booking_id}`)}
              className="text-[11px] text-cyan-700 hover:underline flex items-center gap-1"
              data-testid={`reimb-open-queue-${row.booking_id}`}
            >
              Open in expense queue <ExternalLink className="w-3 h-3" />
            </button>
          </div>

          <div className="mt-2 space-y-2">
            {row.expenses.map(ex => {
              const isBusy = busyId === ex.id;
              const isPending = ex.status === 'pending';
              return (
                <div
                  key={ex.id}
                  data-testid={`reimb-item-${ex.id}`}
                  className="border border-gray-100 rounded-md p-2 bg-gray-50/40"
                >
                  <div className="flex items-start gap-3 text-xs">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-gray-900 capitalize">{ex.expense_type}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${
                          ex.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          ex.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-rose-100 text-rose-700'
                        }`}>
                          {ex.status === 'pending' ? 'Under Review' : ex.status}
                        </span>
                        <span className="font-semibold text-purple-700">${Number(ex.amount).toFixed(2)}</span>
                        {ex.adjusted_amount != null && Number(ex.adjusted_amount) !== Number(ex.amount) && (
                          <span className="text-emerald-700 text-[11px]">
                            → adjusted to <strong>${Number(ex.adjusted_amount).toFixed(2)}</strong>
                          </span>
                        )}
                        {ex.created_at && (
                          <span className="text-[10px] text-gray-400">submitted {new Date(ex.created_at).toLocaleDateString()}</span>
                        )}
                      </div>
                      {ex.notes && (
                        <p className="text-[11px] text-gray-700 mt-1">
                          <span className="font-semibold">Talent note:</span> {ex.notes}
                        </p>
                      )}
                      {ex.admin_notes && (
                        <p className="text-[11px] text-amber-700 mt-0.5">
                          <span className="font-semibold">Admin note:</span> {ex.admin_notes}
                        </p>
                      )}
                      {ex.receipt_url && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/expenses/receipt/${ex.receipt_url}?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-cyan-700 hover:underline mt-1"
                          data-testid={`reimb-receipt-${ex.id}`}
                        >
                          <FileText className="w-3 h-3" /> View receipt
                        </a>
                      )}
                    </div>

                    {isPending && (
                      <div className="flex flex-col gap-1 items-end">
                        <Input
                          placeholder="Admin notes (optional)"
                          value={pendingNotes[ex.id] || ''}
                          onChange={(e) => setPendingNotes(prev => ({ ...prev, [ex.id]: e.target.value }))}
                          className="h-7 text-[11px] w-48"
                          data-testid={`reimb-note-${ex.id}`}
                        />
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            disabled={isBusy}
                            onClick={() => reviewExpense(ex, 'approve')}
                            className="h-7 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`reimb-approve-${ex.id}`}
                          >
                            {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isBusy}
                            onClick={() => reviewExpense(ex, 'reject')}
                            className="h-7 px-2 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                            data-testid={`reimb-reject-${ex.id}`}
                          >
                            <XCircle className="w-3 h-3 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TimesheetsPanel() {
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [approving, setApproving] = useState(false);
  const [breakEdits, setBreakEdits] = useState({}); // booking_id -> minutes (local override)
  const [savingBreak, setSavingBreak] = useState({}); // booking_id -> bool
  // Iter-226d — expanded rows + bulk confirm dialog
  const [expanded, setExpanded] = useState(new Set());
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });
  // Iter-372 (Frontend) — clock-time edit modal
  const [editRow, setEditRow] = useState(null);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientLabel, setClientLabel] = useState('');
  const [textQuery, setTextQuery] = useState('');

  const fetchTimesheets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (clientId) params.set('client_id', clientId);
      const qs = params.toString();
      const res = await fetch(`${API}/api/operations/pending-timesheets${qs ? `?${qs}` : ''}`, { headers: getHeaders() });
      const data = await res.json();
      setTimesheets(data.pending_timesheets || []);
      setSummary(data.summary || {});
      setBreakEdits({});
    } catch { toast.error('Failed to load timesheets'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, clientId]);

  useEffect(() => { fetchTimesheets(); }, [fetchTimesheets]);

  // Client typeahead
  useEffect(() => {
    if (!clientSearch || clientSearch.length < 2) { setClientResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/admin/clients/search?q=${encodeURIComponent(clientSearch)}&limit=8`);
        setClientResults(res.data || []);
      } catch { setClientResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [clientSearch]);

  const filtered = textQuery.trim()
    ? timesheets.filter(t => {
        const q = textQuery.toLowerCase();
        return (
          (t.talent_name || '').toLowerCase().includes(q) ||
          (t.event_name || '').toLowerCase().includes(q) ||
          (t.client_name || '').toLowerCase().includes(q) ||
          (t.venue_name || '').toLowerCase().includes(q) ||
          (t.role_name || '').toLowerCase().includes(q)
        );
      })
    : timesheets;

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(t => t.booking_id)));
  };

  const getBreak = (t) => (breakEdits[t.booking_id] ?? t.break_minutes ?? 0);
  const netHoursFor = (t) => {
    const raw = Number(t.raw_hours || t.hours_worked || 0);
    const brk = Number(getBreak(t));
    return Math.max(0, raw - brk / 60);
  };

  const updateBreakLocal = (bookingId, val) => {
    const n = Math.max(0, Math.min(480, parseInt(val, 10) || 0));
    setBreakEdits(prev => ({ ...prev, [bookingId]: n }));
  };

  const persistBreak = async (bookingId, explicitMinutes) => {
    const minutes = explicitMinutes !== undefined ? explicitMinutes : breakEdits[bookingId];
    if (minutes === undefined) return;
    setSavingBreak(prev => ({ ...prev, [bookingId]: true }));
    try {
      const res = await fetch(`${API}/api/operations/timesheet/${bookingId}/break`, {
        method: 'PATCH',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ break_minutes: minutes }),
      });
      if (res.ok) {
        toast.success('Break updated');
        // reflect into row
        setTimesheets(prev => prev.map(r => r.booking_id === bookingId ? { ...r, break_minutes: minutes } : r));
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Failed to save break');
      }
    } catch { toast.error('Failed to save break'); }
    finally { setSavingBreak(prev => ({ ...prev, [bookingId]: false })); }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const openConfirm = (action) => {
    if (selected.size === 0) {
      toast.error('Select at least one timesheet');
      return;
    }
    setConfirmDialog({ open: true, action });
  };

  const selectedRows = filtered.filter(t => selected.has(t.booking_id));

  const batchApprove = async (approve) => {
    if (selected.size === 0) return toast.error('Select at least one timesheet');
    setApproving(true);
    // Send any pending break edits for the selected rows along with the batch call.
    const overrides = {};
    for (const id of selected) {
      if (breakEdits[id] !== undefined) overrides[id] = breakEdits[id];
    }
    try {
      const res = await fetch(`${API}/api/operations/batch-approve-timesheets`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_ids: Array.from(selected),
          approve,
          break_minutes_overrides: Object.keys(overrides).length ? overrides : undefined,
        })
      });
      const data = await res.json();
      if (res.ok) {
        const verb = approve ? 'Approved' : 'Rejected';
        const doneCount = approve
          ? (data.summary?.approved || 0)
          : (data.summary?.rejected || 0);
        const errList = Array.isArray(data.details?.errors) ? data.details.errors : [];
        // If NOTHING went through, show the real reason(s) — not a silent "Approved: 0"
        if (doneCount === 0 && errList.length > 0) {
          const reasons = Array.from(new Set(errList.map(e => e.error || 'Unknown error')));
          toast.error(
            `Nothing ${approve ? 'approved' : 'rejected'} — ${errList.length} skipped: ${reasons.join(' · ')}`,
            { duration: 8000 }
          );
        } else if (errList.length > 0) {
          // Partial success — show count + first reason so admin knows some were skipped
          const firstReason = errList[0]?.error || 'Unknown error';
          toast.warning(
            `${verb}: ${doneCount} · ${errList.length} skipped (${firstReason})`,
            { duration: 6000 }
          );
        } else {
          toast.success(`${verb}: ${doneCount}`);
        }
        setSelected(new Set());
        setConfirmDialog({ open: false, action: null });
        fetchTimesheets();
      } else toast.error(data.detail || 'Batch action failed');
    } catch { toast.error('Action failed'); }
    finally { setApproving(false); }
  };

  // Sprint 1B — Per-row inline quick action (reuses batch endpoint with a
  // single booking_id so we don't add a new backend route). Used by the
  // mobile cards' inline Approve/Reject buttons.
  const [rowActing, setRowActing] = useState({});
  const quickRowAction = async (bookingId, approve) => {
    setRowActing(prev => ({ ...prev, [bookingId]: true }));
    const overrides = breakEdits[bookingId] !== undefined
      ? { [bookingId]: breakEdits[bookingId] }
      : undefined;
    try {
      const res = await fetch(`${API}/api/operations/batch-approve-timesheets`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_ids: [bookingId],
          approve,
          break_minutes_overrides: overrides,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(approve ? 'Approved' : 'Rejected');
        setSelected(prev => { const n = new Set(prev); n.delete(bookingId); return n; });
        fetchTimesheets();
      } else toast.error(data.detail || 'Action failed');
    } catch { toast.error('Action failed'); }
    finally { setRowActing(prev => ({ ...prev, [bookingId]: false })); }
  };

  const clearFilters = () => {
    setDateFrom(''); setDateTo(''); setClientId(''); setClientLabel(''); setClientSearch(''); setClientResults([]); setTextQuery('');
  };

  const activeFilterCount = (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (clientId ? 1 : 0) + (textQuery ? 1 : 0);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#03DFFC]" /></div>;

  return (
    <div data-testid="timesheets-panel" className="space-y-4">
      {/* Filters */}
      <Card className="border-gray-200 bg-white/70 backdrop-blur-sm">
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Filter className="w-4 h-4 text-cyan-600" /> Filters
              {activeFilterCount > 0 && (
                <Badge className="bg-cyan-100 text-cyan-800 border-cyan-200 text-[10px]">{activeFilterCount} active</Badge>
              )}
            </div>
            {activeFilterCount > 0 && (
              <Button data-testid="clear-filters-btn" variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500 hover:text-gray-900 h-7">
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {/* Date From */}
            <div>
              <Label className="text-[11px] text-gray-500 uppercase tracking-wider mb-1 block">From</Label>
              <Input data-testid="filter-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 bg-white border-gray-200" />
            </div>
            {/* Date To */}
            <div>
              <Label className="text-[11px] text-gray-500 uppercase tracking-wider mb-1 block">To</Label>
              <Input data-testid="filter-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 bg-white border-gray-200" />
            </div>
            {/* Quick date chips */}
            <div className="flex items-end gap-1.5 col-span-1 sm:col-span-2 lg:col-span-1">
              <Button data-testid="filter-today-btn" type="button" size="sm" variant="outline" onClick={() => { setDateFrom(todayIso(0)); setDateTo(todayIso(0)); }} className="h-9 text-xs px-2">Today</Button>
              <Button data-testid="filter-week-btn" type="button" size="sm" variant="outline" onClick={() => { setDateFrom(todayIso(-6)); setDateTo(todayIso(0)); }} className="h-9 text-xs px-2">7d</Button>
              <Button data-testid="filter-month-btn" type="button" size="sm" variant="outline" onClick={() => { setDateFrom(todayIso(-29)); setDateTo(todayIso(0)); }} className="h-9 text-xs px-2">30d</Button>
            </div>
            {/* Client filter */}
            <div className="relative col-span-1 sm:col-span-2 lg:col-span-1">
              <Label className="text-[11px] text-gray-500 uppercase tracking-wider mb-1 block">Client</Label>
              {clientId ? (
                <div className="flex items-center gap-1.5 h-9 px-2 rounded-md border border-cyan-200 bg-cyan-50 text-sm">
                  <Building2 className="w-3.5 h-3.5 text-cyan-700" />
                  <span className="truncate text-cyan-900 flex-1" data-testid="filter-client-active">{clientLabel}</span>
                  <button data-testid="filter-client-clear" type="button" onClick={() => { setClientId(''); setClientLabel(''); }} className="text-cyan-700 hover:text-cyan-900">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <Input
                  data-testid="filter-client-search"
                  placeholder="Search client…"
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                  className="h-9 bg-white border-gray-200"
                />
              )}
              {clientResults.length > 0 && !clientId && (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-auto">
                  {clientResults.map(c => (
                    <button
                      key={c.id}
                      data-testid={`filter-client-pick-${c.id}`}
                      type="button"
                      onClick={() => { setClientId(c.id); setClientLabel(c.display); setClientResults([]); setClientSearch(''); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900 truncate">{c.display}</div>
                      <div className="text-[11px] text-gray-500 truncate">{c.email}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Text search */}
            <div className="col-span-1 sm:col-span-2 lg:col-span-1">
              <Label className="text-[11px] text-gray-500 uppercase tracking-wider mb-1 block">Search</Label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <Input data-testid="filter-text" value={textQuery} onChange={e => setTextQuery(e.target.value)} placeholder="Talent, event, venue…" className="h-9 pl-7 bg-white border-gray-200" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gradient-to-r from-cyan-50 via-white to-amber-50 rounded-xl px-3 sm:px-4 py-3 border border-gray-200">
        <div className="grid grid-cols-2 sm:flex sm:gap-6 gap-2 text-xs sm:text-sm">
          <div className="flex flex-col">
            <span className="text-gray-500 text-[10px] uppercase tracking-wider">Pending</span>
            <span className="text-gray-900 font-semibold text-base" data-testid="summary-pending">{summary.total_pending || 0}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-500 text-[10px] uppercase tracking-wider">Net Hours</span>
            <span className="text-gray-900 font-semibold text-base" data-testid="summary-net-hours">{summary.total_hours || 0}h</span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-500 text-[10px] uppercase tracking-wider">Raw Hours</span>
            <span className="text-gray-700 font-medium text-sm" data-testid="summary-raw-hours">{summary.total_raw_hours || 0}h</span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-500 text-[10px] uppercase tracking-wider">Break Mins</span>
            <span className="text-amber-700 font-medium text-sm" data-testid="summary-break-minutes">{summary.total_break_minutes || 0}</span>
          </div>
          <div className="flex flex-col col-span-2 sm:col-span-1">
            <span className="text-gray-500 text-[10px] uppercase tracking-wider">Hourly Pay</span>
            <span className="text-emerald-600 font-semibold text-base" data-testid="summary-amount">${(summary.total_amount || 0).toLocaleString()}</span>
          </div>
          {/* Iter-232c — Reimbursements settlement breakdown */}
          <div className="flex flex-col">
            <span className="text-gray-500 text-[10px] uppercase tracking-wider">Reimb. Approved</span>
            <span className="text-purple-700 font-medium text-sm" data-testid="summary-reimb-approved">
              ${((summary.settlement?.reimbursements_approved) || 0).toLocaleString()}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-500 text-[10px] uppercase tracking-wider">Reimb. Pending</span>
            <span className="text-amber-700 font-medium text-sm" data-testid="summary-reimb-pending">
              ${((summary.settlement?.reimbursements_pending) || 0).toLocaleString()}
              {summary.settlement?.reimbursements_pending_count > 0 && (
                <span className="ml-1 text-[10px] text-amber-600">
                  · {summary.settlement.reimbursements_pending_count} to review
                </span>
              )}
            </span>
          </div>
          <div className="flex flex-col col-span-2 sm:col-span-1">
            <span className="text-gray-500 text-[10px] uppercase tracking-wider">Total Settlement</span>
            <span className="text-gray-900 font-bold text-base" data-testid="summary-settlement-total">
              ${((summary.settlement?.total_settlement) || summary.total_amount || 0).toLocaleString()}
            </span>
          </div>
        </div>
        <div className="flex gap-2 sm:justify-end">
          <Button data-testid="batch-approve-btn" size="sm" disabled={selected.size === 0 || approving} onClick={() => openConfirm('approve')} className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 sm:flex-none">
            {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" /> Approve ({selected.size})</>}
          </Button>
          <Button data-testid="batch-reject-btn" size="sm" variant="destructive" disabled={selected.size === 0 || approving} onClick={() => openConfirm('reject')} className="flex-1 sm:flex-none">
            Reject ({selected.size})
          </Button>
        </div>
      </div>

      {/* Iter-226d — Bulk approve/reject confirmation modal */}
      <BulkConfirmDialog
        open={confirmDialog.open}
        action={confirmDialog.action}
        selectedTimesheets={selectedRows}
        isApproving={approving}
        onClose={() => setConfirmDialog({ open: false, action: null })}
        onConfirm={() => batchApprove(confirmDialog.action === 'approve')}
      />

      {/* Iter-372 (Frontend) — Timesheet edit modal */}
      <TimesheetEditModal
        open={!!editRow}
        row={editRow}
        onClose={() => setEditRow(null)}
        onSaved={() => { setEditRow(null); fetchTimesheets(); }}
      />

      {filtered.length === 0 ? (
        <div className="text-center py-10 text-gray-500" data-testid="timesheets-empty">
          <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">{timesheets.length === 0 ? 'No pending timesheets' : 'No timesheets match your filters'}</p>
        </div>
      ) : (
        <>
          {/* Select all */}
          <div className="flex items-center justify-between px-1 sm:px-3">
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <Checkbox checked={selected.size === filtered.length && filtered.length > 0} onCheckedChange={selectAll} data-testid="select-all-timesheets" />
              Select all {filtered.length}
            </label>
            <span className="text-[11px] text-gray-500">{selected.size} selected</span>
          </div>

          {/* Desktop table */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-[28px_24px_1.4fr_1.6fr_1.2fr_104px_90px_90px_90px_80px_110px_110px] gap-3 px-3 py-2 text-[10px] text-gray-500 uppercase tracking-wider border-b border-gray-200">
              <div></div>
              <div></div>
              <div>Talent</div>
              <div>Event / Venue</div>
              <div>Client</div>
              <div>Date</div>
              <div>Clock In</div>
              <div>Clock Out</div>
              <div>Hours</div>
              <div>Break</div>
              <div>Amount</div>
              <div>Status</div>
            </div>
            <div className="space-y-1 mt-1">
              {filtered.map(t => {
                const brk = getBreak(t);
                const net = netHoursFor(t);
                const dirty = breakEdits[t.booking_id] !== undefined && breakEdits[t.booking_id] !== t.break_minutes;
                const isOpen = expanded.has(t.booking_id);
                return (
                  <div key={t.booking_id} className={`rounded-lg ${selected.has(t.booking_id) ? 'bg-cyan-50 border border-cyan-200' : 'bg-white border border-gray-100 hover:bg-gray-50'} transition-colors`}>
                    <div
                      data-testid={`timesheet-row-${t.booking_id}`}
                      className="grid grid-cols-[28px_24px_1.4fr_1.6fr_1.2fr_104px_90px_90px_90px_80px_110px_110px] gap-3 px-3 py-3 items-center"
                    >
                      <Checkbox checked={selected.has(t.booking_id)} onCheckedChange={() => toggleSelect(t.booking_id)} data-testid={`row-select-${t.booking_id}`} />
                      <button
                        type="button"
                        onClick={() => toggleExpand(t.booking_id)}
                        data-testid={`row-expand-${t.booking_id}`}
                        className="text-gray-500 hover:text-cyan-700 rounded p-0.5 hover:bg-cyan-50"
                        aria-label={isOpen ? 'Collapse details' : 'Expand details'}
                      >
                        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{t.talent_name}</p>
                        <p className="text-[11px] text-gray-500 truncate">{t.role_name}</p>
                      </div>
                      <div className="min-w-0">
                        <button
                          type="button"
                          data-testid={`event-link-${t.booking_id}`}
                          onClick={() => t.shift_id && navigate(`/admin/shifts/${t.shift_id}`)}
                          disabled={!t.shift_id}
                          className="text-sm font-medium text-gray-900 truncate flex items-center gap-1 hover:text-cyan-700 hover:underline disabled:cursor-not-allowed disabled:hover:text-gray-900 disabled:hover:no-underline text-left w-full"
                          title={t.shift_id ? 'Open shift details' : ''}
                        >
                          <Sparkles className="w-3 h-3 text-cyan-500 shrink-0" />
                          <span className="truncate">{t.event_name}</span>
                          {t.shift_id && <ExternalLink className="w-3 h-3 text-gray-400 group-hover:text-cyan-700 shrink-0 ml-0.5" />}
                        </button>
                        <p className="text-[11px] text-gray-500 truncate flex items-center gap-1">
                          <MapPin className="w-3 h-3 shrink-0" /> {t.venue_name} · {t.shift_date}
                        </p>
                        <FlagChips flags={t.flags} />
                      </div>
                      <div className="min-w-0 flex items-center gap-1">
                        <Building2 className="w-3 h-3 text-gray-400 shrink-0" />
                        <span className="text-sm text-gray-800 truncate">{t.client_name}</span>
                      </div>
                      <div className="min-w-0" data-testid={`row-date-${t.booking_id}`}>
                        <p className="text-xs font-semibold text-gray-900 flex items-center gap-1">
                          <CalendarDays className="w-3 h-3 text-cyan-500 shrink-0" />
                          {t.shift_date
                            ? new Date(t.shift_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                            : '—'}
                        </p>
                        {t.shift_date && (
                          <p className="text-[10px] text-gray-400">
                            {new Date(t.shift_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-gray-700"><LogIn className="w-3 h-3 inline mr-0.5 text-emerald-500" />{t.clock_in || '—'}</span>
                      <span className="text-xs text-gray-700"><LogOut className="w-3 h-3 inline mr-0.5 text-red-500" />{t.clock_out || '—'}</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{net.toFixed(2)}h</p>
                        {brk > 0 && <p className="text-[10px] text-gray-500 line-through">{(t.raw_hours || 0).toFixed(2)}h</p>}
                      </div>
                      <div className="flex items-center gap-1">
                        <Coffee className="w-3 h-3 text-amber-500 shrink-0" />
                        <Input
                          type="number"
                          min={0}
                          max={480}
                          value={brk}
                          onChange={e => updateBreakLocal(t.booking_id, e.target.value)}
                          onBlur={() => dirty && persistBreak(t.booking_id)}
                          className={`h-7 px-1.5 text-xs w-14 ${dirty ? 'border-amber-400 ring-1 ring-amber-200' : ''}`}
                          data-testid={`break-input-${t.booking_id}`}
                        />
                        {savingBreak[t.booking_id] && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
                      </div>
                      <span className="text-sm text-emerald-600 font-semibold">${(net * (t.pay_rate || 0)).toFixed(2)}</span>
                      <div className="flex items-center gap-1">
                        <StatusBadge status={t.status} />
                        {/* Iter-232c — Reimbursement chip surfaces pending receipts inline */}
                        {(t.expense_summary?.pending_count || 0) > 0 && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setExpanded(prev => { const n = new Set(prev); n.add(t.booking_id); return n; }); }}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-purple-100 text-purple-700 border border-purple-200 hover:bg-purple-200"
                            data-testid={`reimb-chip-${t.booking_id}`}
                            title="Pending reimbursements — click to review"
                          >
                            <Receipt className="w-3 h-3" />
                            {t.expense_summary.pending_count} · ${Number(t.expense_summary.pending_total || 0).toFixed(0)}
                          </button>
                        )}
                      </div>
                    </div>
                    {isOpen && <TimesheetDetailDrawer row={t} onNavigate={navigate} onChange={fetchTimesheets} onEditTimes={setEditRow} />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile card layout — Sprint 1B: date-grouped, urgency-bordered,
              inline Approve/Reject, OT chip, prominent reimb chip, +/− break stepper. */}
          <div className="lg:hidden space-y-4 pb-24">
            {(() => {
              // Group by shift_date (most recent first) and render a sticky header per date.
              const groups = filtered.reduce((acc, row) => {
                const key = row.shift_date || 'undated';
                (acc[key] = acc[key] || []).push(row);
                return acc;
              }, {});
              const sortedDates = Object.keys(groups).sort((a, b) => b.localeCompare(a));
              return sortedDates.map(dateKey => (
                <div key={dateKey} data-testid={`mobile-date-group-${dateKey}`}>
                  <div
                    className="sticky top-0 z-10 -mx-1 px-3 py-2 mb-2 bg-gradient-to-r from-slate-100 to-white border-b border-slate-200 flex items-center justify-between"
                    data-testid={`mobile-date-header-${dateKey}`}
                  >
                    <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                      <CalendarDays className="w-3.5 h-3.5 text-cyan-600" />
                      {dateGroupLabel(dateKey)}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {groups[dateKey].length} timesheet{groups[dateKey].length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {groups[dateKey].map(t => {
                      const brk = getBreak(t);
                      const net = netHoursFor(t);
                      const dirty = breakEdits[t.booking_id] !== undefined && breakEdits[t.booking_id] !== t.break_minutes;
                      const otHours = Number(t.overtime_hours || 0);
                      const isOT = otHours > 0 || (t.flags || {}).overtime;
                      const reimbPending = Number(t.expense_summary?.pending_count || 0);
                      const reimbTotal = Number(t.expense_summary?.pending_total || 0);
                      const acting = !!rowActing[t.booking_id];
                      return (
                        <Card
                          key={t.booking_id}
                          data-testid={`timesheet-card-${t.booking_id}`}
                          style={urgencyBorderStyle(t.shift_date)}
                          className={`${selected.has(t.booking_id) ? 'ring-1 ring-cyan-200 bg-cyan-50/40' : 'bg-white'} border-y border-r border-gray-200 transition-colors`}
                        >
                          <CardContent className="p-3 space-y-3">
                            {/* Header: checkbox + talent + status */}
                            <div className="flex items-start gap-3">
                              <Checkbox
                                checked={selected.has(t.booking_id)}
                                onCheckedChange={() => toggleSelect(t.booking_id)}
                                className="mt-1 w-5 h-5"
                                data-testid={`row-select-${t.booking_id}`}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="text-base font-semibold text-gray-900 truncate">{t.talent_name}</p>
                                    <p className="text-[11px] text-gray-500">{t.role_name}</p>
                                  </div>
                                  <div className="flex flex-col items-end gap-1">
                                    <StatusBadge status={t.status} />
                                    {daysAwaiting(t.shift_date) > 7 && (
                                      <span
                                        className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-md bg-red-100 text-red-700 border border-red-200"
                                        data-testid={`urgency-overdue-${t.booking_id}`}
                                      >
                                        {daysAwaiting(t.shift_date)}d overdue
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="mt-2 flex items-center gap-1.5 text-sm text-gray-800">
                                  <Sparkles className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
                                  <button
                                    type="button"
                                    onClick={() => t.shift_id && navigate(`/admin/shifts/${t.shift_id}`)}
                                    disabled={!t.shift_id}
                                    data-testid={`mobile-event-link-${t.booking_id}`}
                                    className="truncate font-medium text-left hover:text-cyan-700 hover:underline disabled:hover:text-gray-800 disabled:hover:no-underline disabled:cursor-not-allowed flex-1"
                                  >
                                    {t.event_name}
                                  </button>
                                  {t.shift_id && <ExternalLink className="w-3 h-3 text-gray-400 shrink-0" />}
                                </div>
                                <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                                  <MapPin className="w-3 h-3 shrink-0" /><span className="truncate">{t.venue_name}</span>
                                </div>
                                <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-700">
                                  <Building2 className="w-3 h-3 text-gray-400 shrink-0" /><span className="truncate">{t.client_name}</span>
                                </div>
                                <FlagChips flags={t.flags} />
                              </div>
                            </div>

                            {/* Clock In / Clock Out / Hours / Amount tiles */}
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="bg-gray-50 rounded-md p-2">
                                <p className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1"><LogIn className="w-3 h-3 text-emerald-500" />Clock In</p>
                                <p className="text-sm font-medium text-gray-900">{t.clock_in || '—'}</p>
                              </div>
                              <div className="bg-gray-50 rounded-md p-2">
                                <p className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1"><LogOut className="w-3 h-3 text-red-500" />Clock Out</p>
                                <p className="text-sm font-medium text-gray-900">{t.clock_out || '—'}</p>
                              </div>
                              <div className="bg-gray-50 rounded-md p-2">
                                <p className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1"><Hourglass className="w-3 h-3 text-cyan-500" />Hours</p>
                                <div className="flex items-baseline gap-1.5 flex-wrap">
                                  <p className="text-sm font-semibold text-gray-900">{net.toFixed(2)}h</p>
                                  {isOT && (
                                    <span
                                      className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-md bg-purple-100 text-purple-700 border border-purple-200"
                                      data-testid={`mobile-ot-chip-${t.booking_id}`}
                                      title={`Overtime: ${otHours.toFixed(2)}h`}
                                    >
                                      OT{otHours > 0 ? ` · ${otHours.toFixed(1)}h` : ''}
                                    </span>
                                  )}
                                </div>
                                {brk > 0 && <p className="text-[10px] text-gray-500 line-through">{(t.raw_hours || 0).toFixed(2)}h raw</p>}
                              </div>
                              <div className="bg-gray-50 rounded-md p-2">
                                <p className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1"><DollarSign className="w-3 h-3 text-emerald-500" />Amount</p>
                                <p className="text-sm font-semibold text-emerald-600">${(net * (t.pay_rate || 0)).toFixed(2)}</p>
                              </div>
                            </div>

                            {/* Sprint 1B — Prominent reimbursement pending chip on mobile */}
                            {reimbPending > 0 && (
                              <button
                                type="button"
                                onClick={() => setExpanded(prev => { const n = new Set(prev); n.add(t.booking_id); return n; })}
                                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-purple-50 border border-purple-200 hover:bg-purple-100 active:bg-purple-200 transition-colors"
                                data-testid={`mobile-reimb-chip-${t.booking_id}`}
                              >
                                <span className="flex items-center gap-2 text-[12px] font-semibold text-purple-800 whitespace-nowrap">
                                  <Receipt className="w-4 h-4 shrink-0" />
                                  {reimbPending} reimbursement{reimbPending === 1 ? '' : 's'} pending
                                </span>
                                <span className="text-[12px] font-bold text-purple-700 whitespace-nowrap">· ${reimbTotal.toFixed(2)}</span>
                              </button>
                            )}

                            {/* Sprint 1B — 15-minute break stepper */}
                            <MobileBreakStepper
                              value={brk}
                              saving={!!savingBreak[t.booking_id]}
                              onChange={(v) => updateBreakLocal(t.booking_id, v)}
                              onCommit={(v) => {
                                setBreakEdits(prev => ({ ...prev, [t.booking_id]: v }));
                                // Persist immediately if changed from server value.
                                // Pass v explicitly to avoid stale-closure read of breakEdits.
                                if (v !== (t.break_minutes ?? 0)) persistBreak(t.booking_id, v);
                              }}
                              testIdPrefix={`mobile-break-${t.booking_id}`}
                            />

                            {dirty && (
                              <p className="text-[10px] text-amber-700 italic">Break edited — will apply on Approve.</p>
                            )}

                            {/* Sprint 1B — Inline Approve / Reject quick actions (mobile only) */}
                            <div className="grid grid-cols-2 gap-2">
                              <Button
                                type="button"
                                size="sm"
                                disabled={acting}
                                onClick={() => quickRowAction(t.booking_id, true)}
                                className="min-h-11 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white"
                                data-testid={`mobile-row-approve-${t.booking_id}`}
                              >
                                {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" /> Approve</>}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="destructive"
                                disabled={acting}
                                onClick={() => quickRowAction(t.booking_id, false)}
                                className="min-h-11"
                                data-testid={`mobile-row-reject-${t.booking_id}`}
                              >
                                {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-1" /> Reject</>}
                              </Button>
                            </div>

                            <button
                              type="button"
                              onClick={() => toggleExpand(t.booking_id)}
                              data-testid={`mobile-row-expand-${t.booking_id}`}
                              className="w-full min-h-11 flex items-center justify-center gap-1 text-[11px] text-cyan-700 hover:text-cyan-900 active:bg-cyan-100 py-2 border border-cyan-100 hover:bg-cyan-50 rounded-md transition-colors"
                            >
                              {expanded.has(t.booking_id) ? <><ChevronUp className="w-3 h-3" /> Hide details</> : <><ChevronDown className="w-3 h-3" /> View details, GPS, selfie, more actions</>}
                            </button>
                            {expanded.has(t.booking_id) && <TimesheetDetailDrawer row={t} onNavigate={navigate} onChange={fetchTimesheets} onEditTimes={setEditRow} />}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>

          {/* Sprint 1B — Sticky bulk action bar (mobile only, only when 1+ selected) */}
          {selected.size > 0 && (
            <div
              className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]"
              style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
              data-testid="mobile-sticky-bulk-bar"
            >
              <div className="px-3 py-2.5 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-gray-500">Selected</p>
                  <p className="text-sm font-semibold text-gray-900" data-testid="mobile-sticky-selected-count">
                    {selected.size} timesheet{selected.size === 1 ? '' : 's'}
                  </p>
                </div>
                <Button
                  size="sm"
                  disabled={approving}
                  onClick={() => openConfirm('approve')}
                  className="min-h-11 bg-emerald-600 hover:bg-emerald-700 text-white px-4"
                  data-testid="mobile-sticky-approve-btn"
                >
                  {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" /> Approve {selected.size}</>}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={approving}
                  onClick={() => openConfirm('reject')}
                  className="min-h-11 px-4"
                  data-testid="mobile-sticky-reject-btn"
                >
                  Reject
                </Button>
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className="min-w-11 min-h-11 flex items-center justify-center text-gray-500 hover:text-gray-900"
                  data-testid="mobile-sticky-clear-btn"
                  aria-label="Clear selection"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Shift Duplication Panel ───────────────────────────────
function DuplicateShiftPanel() {
  const [shiftId, setShiftId] = useState('');
  const [newDate, setNewDate] = useState('');
  const [copyBookings, setCopyBookings] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [result, setResult] = useState(null);

  const handleDuplicate = async () => {
    if (!shiftId || !newDate) return toast.error('Enter shift ID and new date');
    setDuplicating(true);
    try {
      const res = await fetch(`${API}/api/operations/duplicate-shift`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ shift_id: shiftId, new_date: newDate, copy_bookings: copyBookings })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Shift duplicated');
        setResult(data);
      } else toast.error(data.detail || 'Duplication failed');
    } catch { toast.error('Duplication failed'); }
    finally { setDuplicating(false); }
  };

  return (
    <div data-testid="duplicate-shift-panel" className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-gray-500 text-xs mb-1 block">Source Shift ID</Label>
          <Input data-testid="duplicate-shift-id" value={shiftId} onChange={e => setShiftId(e.target.value)} placeholder="Paste shift UUID" className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-600" />
        </div>
        <div>
          <Label className="text-gray-500 text-xs mb-1 block">New Date</Label>
          <Input data-testid="duplicate-shift-date" type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="bg-gray-50 border-gray-200 text-gray-900" />
        </div>
        <div className="flex items-end gap-3">
          <div className="flex items-center gap-2">
            <Checkbox id="copy-bookings" checked={copyBookings} onCheckedChange={setCopyBookings} data-testid="copy-bookings-checkbox" />
            <Label htmlFor="copy-bookings" className="text-gray-500 text-sm">Copy bookings</Label>
          </div>
          <Button data-testid="duplicate-shift-btn" onClick={handleDuplicate} disabled={duplicating} className="bg-indigo-600 hover:bg-indigo-700 text-gray-900">
            {duplicating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Copy className="w-4 h-4 mr-1" /> Duplicate</>}
          </Button>
        </div>
      </div>

      {result && (
        <Card className="bg-green-500/10 border-green-500/20">
          <CardContent className="p-4 text-sm">
            <p className="text-green-600 font-medium">Shift duplicated successfully</p>
            <p className="text-gray-500 mt-1">New Shift ID: <code className="text-green-700 bg-gray-50 px-2 py-0.5 rounded text-xs">{result.new_shift_id}</code></p>
            <p className="text-gray-500">Date: {result.new_date} | Time: {formatTimeRange(result.start_time, result.end_time)} {result.bookings_copied > 0 && `| ${result.bookings_copied} bookings copied`}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Overtime Calculator Panel ─────────────────────────────
function OvertimePanel() {
  const [bookingId, setBookingId] = useState('');
  const [threshold, setThreshold] = useState('8');
  const [multiplier, setMultiplier] = useState('1.5');
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);

  const calculate = async () => {
    if (!bookingId) return toast.error('Enter a booking ID');
    setCalculating(true);
    try {
      const res = await fetch(
        `${API}/api/operations/calculate-overtime/${bookingId}?overtime_threshold_hours=${threshold}&overtime_multiplier=${multiplier}`,
        { method: 'POST', headers: getHeaders() }
      );
      const data = await res.json();
      if (res.ok) {
        setResult(data);
        toast.success('Overtime calculated');
      } else toast.error(data.detail || 'Calculation failed');
    } catch { toast.error('Calculation failed'); }
    finally { setCalculating(false); }
  };

  return (
    <div data-testid="overtime-panel" className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <Label className="text-gray-500 text-xs mb-1 block">Booking ID</Label>
          <Input data-testid="overtime-booking-id" value={bookingId} onChange={e => setBookingId(e.target.value)} placeholder="Booking UUID" className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-600" />
        </div>
        <div>
          <Label className="text-gray-500 text-xs mb-1 block">Threshold (hrs)</Label>
          <Input data-testid="overtime-threshold" type="number" value={threshold} onChange={e => setThreshold(e.target.value)} className="bg-gray-50 border-gray-200 text-gray-900" />
        </div>
        <div>
          <Label className="text-gray-500 text-xs mb-1 block">Multiplier</Label>
          <Input data-testid="overtime-multiplier" type="number" step="0.1" value={multiplier} onChange={e => setMultiplier(e.target.value)} className="bg-gray-50 border-gray-200 text-gray-900" />
        </div>
        <div className="flex items-end">
          <Button data-testid="calculate-overtime-btn" onClick={calculate} disabled={calculating} className="bg-amber-600 hover:bg-amber-700 text-gray-900 w-full">
            {calculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Timer className="w-4 h-4 mr-1" /> Calculate</>}
          </Button>
        </div>
      </div>

      {result && (
        <Card className="bg-gray-50/60 border-gray-200">
          <CardContent className="p-4">
            <p className="text-gray-900 font-medium mb-3">{result.talent_name}</p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Hours</p>
                <p className="text-gray-900">Regular: {result.hours?.regular}h</p>
                <p className="text-amber-500">Overtime: {result.hours?.overtime}h</p>
                <p className="text-[#03DFFC] font-semibold">Total: {result.hours?.total}h</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Talent Pay</p>
                <p className="text-gray-900">${result.pay?.regular}</p>
                <p className="text-amber-500">${result.pay?.overtime}</p>
                <p className="text-[#03DFFC] font-semibold">${result.pay?.total}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Client Bill</p>
                <p className="text-gray-900">${result.bill?.regular}</p>
                <p className="text-amber-500">${result.bill?.overtime}</p>
                <p className="text-green-600 font-semibold">${result.bill?.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────
export default function OperationsDashboard() {
  const navigate = useNavigate();
  const [quickActions, setQuickActions] = useState(null);
  // Iter-248 — IDV Expiration tile counts
  const [idvExpStats, setIdvExpStats] = useState({ expired: 0, expiring_30d: 0, expiring_60d: 0 });
  // Iter-248 — Live Command Center pending shifts count
  const [liveOpsActive, setLiveOpsActive] = useState(0);
  // Iter-249 — Today's Celebrations widget
  const [celebrations, setCelebrations] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter-232c — accept ?tab=timesheets|no-shows|... so deep-links from Home V2
  // (e.g. "Timesheet Approvals" tile) land on the right tab.
  const initialTab = (() => {
    try {
      const p = new URLSearchParams(window.location.search).get('tab');
      return ['no-shows', 'timesheets', 'approved-payments', 'duplicate', 'overtime', 'data-maintenance'].includes(p) ? p : 'no-shows';
    } catch { return 'no-shows'; }
  })();
  const [activeTab, setActiveTab] = useState(initialTab);

  const fetchQuickActions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/operations/dashboard/quick-actions`, { headers: getHeaders() });
      const data = await res.json();
      setQuickActions(data.quick_actions || {});
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  // Iter-248 — Load IDV expiration counts + Live Ops shift count in parallel
  const fetchSecondaryTiles = useCallback(async () => {
    try {
      const [expRes, loRes, celRes] = await Promise.allSettled([
        api.get('/admin/idv/expiration-stats'),
        api.get('/admin/live-ops/today'),
        api.get('/community/celebrations/today'),  // Iter-249
      ]);
      if (expRes.status === 'fulfilled') setIdvExpStats(expRes.value.data || {});
      if (loRes.status === 'fulfilled') setLiveOpsActive(loRes.value.data?.shift_count || 0);
      if (celRes.status === 'fulfilled') setCelebrations(celRes.value.data || null);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchQuickActions(); fetchSecondaryTiles(); }, [fetchQuickActions, fetchSecondaryTiles]);

  const qa = quickActions || {};

  return (
    <div className="min-h-screen bg-white" data-testid="operations-dashboard">
      {/* Header */}
      <header className="bg-gray-50 border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/home')} className="text-gray-500 hover:text-gray-900 hover:bg-gray-100" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Zap className="w-5 h-5 text-amber-500" />
            <h1 className="text-lg font-semibold text-gray-900">Operations Center</h1>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchQuickActions} className="text-gray-500 hover:text-gray-900" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Iter-246c — "Daily Workflows" row.
            Mel feedback: ID Verification, GGF Audit, and Recap review are
            operational tools admins use daily, not configuration. They
            belong here in Operations Center, not buried in Settings. */}
        <section data-testid="ops-daily-workflows">
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Daily Workflows</h2>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            {/* Iter-248 — Live Event Command Center (GPS monitoring) */}
            <button
              onClick={() => navigate('/admin/live-ops')}
              className="flex items-center gap-3 p-3 rounded-2xl border border-cyan-300 bg-gradient-to-br from-cyan-50 to-blue-50/40 hover:from-cyan-100 hover:to-blue-100/60 hover:border-cyan-400 transition text-left shadow-sm"
              data-testid="ops-quick-live-ops"
            >
              <div className="w-10 h-10 rounded-xl bg-cyan-100 grid place-items-center text-cyan-700 shrink-0 relative">
                <Radio className="w-5 h-5" />
                {liveOpsActive > 0 && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-cyan-500 rounded-full ring-2 ring-white animate-pulse" />
                )}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1">
                  Live Command Center
                  {liveOpsActive > 0 && (
                    <Badge className="bg-cyan-600 text-white text-[10px] px-1.5 py-0">{liveOpsActive}</Badge>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 truncate">GPS map · check-ins · AI alerts</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 ml-auto shrink-0" />
            </button>

            <button
              onClick={() => navigate('/admin/idv-review')}
              className="flex items-center gap-3 p-3 rounded-2xl border border-amber-200 bg-amber-50/40 hover:bg-amber-50 hover:border-amber-300 transition text-left"
              data-testid="ops-quick-idv-review"
            >
              <div className="w-10 h-10 rounded-xl bg-amber-100 grid place-items-center text-amber-700 shrink-0">
                <FileCheck className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">ID Verification Review</div>
                <div className="text-[11px] text-slate-500 truncate">Approve / reject talent IDV submissions</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 ml-auto shrink-0" />
            </button>

            {/* Iter-248 — Expiring IDV dashboard tile */}
            <button
              onClick={() => navigate('/admin/idv-review?view=expiring')}
              className={`flex items-center gap-3 p-3 rounded-2xl border transition text-left ${
                idvExpStats.expired > 0
                  ? 'border-rose-300 bg-rose-50/40 hover:bg-rose-50'
                  : idvExpStats.expiring_30d > 0
                  ? 'border-amber-300 bg-amber-50/40 hover:bg-amber-50'
                  : 'border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50'
              }`}
              data-testid="ops-quick-idv-expiring"
            >
              <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
                idvExpStats.expired > 0
                  ? 'bg-rose-100 text-rose-700'
                  : idvExpStats.expiring_30d > 0
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-emerald-100 text-emerald-700'
              }`}>
                <AlertCircle className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">Expiring IDs</div>
                <div className="text-[11px] text-slate-500 truncate flex gap-1.5 mt-0.5">
                  {idvExpStats.expired > 0 && (
                    <span className="text-rose-700 font-semibold" data-testid="idv-exp-expired-count">
                      {idvExpStats.expired} expired
                    </span>
                  )}
                  <span className="text-amber-700 font-medium" data-testid="idv-exp-30d-count">
                    {idvExpStats.expiring_30d} in 30d
                  </span>
                  <span className="text-cyan-700" data-testid="idv-exp-60d-count">
                    {idvExpStats.expiring_60d} in 60d
                  </span>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 ml-auto shrink-0" />
            </button>

            <button
              onClick={() => navigate('/admin/ggf/audit')}
              className="flex items-center gap-3 p-3 rounded-2xl border border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50 hover:border-emerald-300 transition text-left"
              data-testid="ops-quick-ggf-audit"
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-100 grid place-items-center text-emerald-700 shrink-0">
                <BadgeCheck className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">GGF Certification Audit</div>
                <div className="text-[11px] text-slate-500 truncate">Coverage, attempts, manual overrides</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 ml-auto shrink-0" />
            </button>

            <button
              onClick={() => navigate('/admin/recaps')}
              className="flex items-center gap-3 p-3 rounded-2xl border border-cyan-200 bg-cyan-50/40 hover:bg-cyan-50 hover:border-cyan-300 transition text-left"
              data-testid="ops-quick-recaps"
            >
              <div className="w-10 h-10 rounded-xl bg-cyan-100 grid place-items-center text-cyan-700 shrink-0">
                <MessageSquareQuote className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">Post-Event Recaps</div>
                <div className="text-[11px] text-slate-500 truncate">Talent recap submissions & moderation</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 ml-auto shrink-0" />
            </button>

            {/* Iter-253 — Creator Network application review queue */}
            <button
              onClick={() => navigate('/admin/creator-applications')}
              className="flex items-center gap-3 p-3 rounded-2xl border border-purple-200 bg-purple-50/40 hover:bg-purple-50 hover:border-purple-300 transition text-left"
              data-testid="ops-quick-creator-apps"
            >
              <div className="w-10 h-10 rounded-xl bg-purple-100 grid place-items-center text-purple-700 shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">Creator Network Applications</div>
                <div className="text-[11px] text-slate-500 truncate">Review, approve, reject creator applicants</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 ml-auto shrink-0" />
            </button>

            <button
              onClick={() => navigate('/admin/certifications')}
              className="flex items-center gap-3 p-3 rounded-2xl border border-violet-200 bg-violet-50/40 hover:bg-violet-50 hover:border-violet-300 transition text-left"
              data-testid="ops-quick-certifications"
            >
              <div className="w-10 h-10 rounded-xl bg-violet-100 grid place-items-center text-violet-700 shrink-0">
                <Shield className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">Certifications</div>
                <div className="text-[11px] text-slate-500 truncate">Training modules & assessment results</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 ml-auto shrink-0" />
            </button>
          </div>
        </section>

        {/* Iter-249 — Today's Celebrations ribbon */}
        {celebrations && (celebrations.totals?.birthdays + celebrations.totals?.first_bookings +
                          celebrations.totals?.certifications + celebrations.totals?.milestones) > 0 && (
          <section data-testid="todays-celebrations">
            <div className="rounded-2xl border border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 via-pink-50 to-amber-50 p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-fuchsia-900 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-fuchsia-600" />
                  Today's Celebrations
                </h2>
                <button
                  onClick={() => navigate('/community')}
                  className="text-[11px] font-medium text-fuchsia-700 hover:underline"
                  data-testid="celebrations-view-feed"
                >
                  Open Community Feed →
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                {/* Birthdays */}
                <div className="rounded-xl bg-white border border-fuchsia-100 p-3" data-testid="celebrate-birthdays">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Cake className="w-4 h-4 text-fuchsia-600" />
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-fuchsia-700">Birthdays</span>
                  </div>
                  <div className="text-2xl font-bold text-fuchsia-900">{celebrations.totals.birthdays}</div>
                  <div className="mt-1 flex -space-x-1.5">
                    {celebrations.birthdays.slice(0,5).map(b => (
                      b.photo
                        ? <img key={b.user_id} src={b.photo} alt={b.name}
                               className="w-6 h-6 rounded-full ring-2 ring-white object-cover" />
                        : <div key={b.user_id}
                               className="w-6 h-6 rounded-full ring-2 ring-white bg-fuchsia-400 text-white text-[10px] font-bold grid place-items-center">
                            {b.name?.[0]?.toUpperCase() || '?'}
                          </div>
                    ))}
                  </div>
                  {celebrations.birthdays.length > 0 && (
                    <div className="text-[10px] text-fuchsia-700 mt-1 truncate">
                      {celebrations.birthdays.slice(0,2).map(b => b.name).join(', ')}
                      {celebrations.birthdays.length > 2 && ` +${celebrations.birthdays.length - 2}`}
                    </div>
                  )}
                </div>

                {/* First bookings */}
                <div className="rounded-xl bg-white border border-amber-100 p-3" data-testid="celebrate-first-bookings">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Trophy className="w-4 h-4 text-amber-600" />
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">First Bookings</span>
                  </div>
                  <div className="text-2xl font-bold text-amber-900">{celebrations.totals.first_bookings}</div>
                  <div className="mt-1 flex -space-x-1.5">
                    {celebrations.first_bookings.slice(0,5).map(b => (
                      b.photo
                        ? <img key={b.user_id} src={b.photo} alt={b.name}
                               className="w-6 h-6 rounded-full ring-2 ring-white object-cover" />
                        : <div key={b.user_id}
                               className="w-6 h-6 rounded-full ring-2 ring-white bg-amber-400 text-white text-[10px] font-bold grid place-items-center">
                            {b.name?.[0]?.toUpperCase() || '?'}
                          </div>
                    ))}
                  </div>
                  {celebrations.first_bookings.length > 0 && (
                    <div className="text-[10px] text-amber-700 mt-1 truncate">
                      {celebrations.first_bookings.slice(0,2).map(b => b.name).join(', ')}
                      {celebrations.first_bookings.length > 2 && ` +${celebrations.first_bookings.length - 2}`}
                    </div>
                  )}
                </div>

                {/* Certifications */}
                <div className="rounded-xl bg-white border border-emerald-100 p-3" data-testid="celebrate-certifications">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Award className="w-4 h-4 text-emerald-600" />
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">Certifications</span>
                  </div>
                  <div className="text-2xl font-bold text-emerald-900">{celebrations.totals.certifications}</div>
                  <div className="mt-1 flex -space-x-1.5">
                    {celebrations.certifications.slice(0,5).map(b => (
                      b.photo
                        ? <img key={b.user_id} src={b.photo} alt={b.name}
                               className="w-6 h-6 rounded-full ring-2 ring-white object-cover" />
                        : <div key={b.user_id}
                               className="w-6 h-6 rounded-full ring-2 ring-white bg-emerald-400 text-white text-[10px] font-bold grid place-items-center">
                            {b.name?.[0]?.toUpperCase() || '?'}
                          </div>
                    ))}
                  </div>
                  {celebrations.certifications.length > 0 && (
                    <div className="text-[10px] text-emerald-700 mt-1 truncate">
                      {celebrations.certifications.slice(0,2).map(b => b.name).join(', ')}
                      {celebrations.certifications.length > 2 && ` +${celebrations.certifications.length - 2}`}
                    </div>
                  )}
                </div>

                {/* Milestones (5-star reviews) */}
                <div className="rounded-xl bg-white border border-cyan-100 p-3" data-testid="celebrate-milestones">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Star className="w-4 h-4 text-cyan-600 fill-cyan-400" />
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-cyan-700">5★ Reviews</span>
                  </div>
                  <div className="text-2xl font-bold text-cyan-900">{celebrations.totals.milestones}</div>
                  <div className="mt-1 flex -space-x-1.5">
                    {celebrations.milestones.slice(0,5).map(b => (
                      b.photo
                        ? <img key={b.user_id} src={b.photo} alt={b.name}
                               className="w-6 h-6 rounded-full ring-2 ring-white object-cover" />
                        : <div key={b.user_id}
                               className="w-6 h-6 rounded-full ring-2 ring-white bg-cyan-400 text-white text-[10px] font-bold grid place-items-center">
                            {b.name?.[0]?.toUpperCase() || '?'}
                          </div>
                    ))}
                  </div>
                  {celebrations.milestones.length > 0 && (
                    <div className="text-[10px] text-cyan-700 mt-1 truncate">
                      {celebrations.milestones.slice(0,2).map(b => b.name).join(', ')}
                      {celebrations.milestones.length > 2 && ` +${celebrations.milestones.length - 2}`}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Quick Action Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <QuickActionCard icon={UserX} label="No-Show Checks" count={qa.noshow_check_needed || 0} color="red" onClick={() => setActiveTab('no-shows')} testId="qa-noshow" />
          <QuickActionCard icon={ClipboardList} label="Pending Timesheets" count={qa.pending_timesheets || 0} color="amber" onClick={() => setActiveTab('timesheets')} testId="qa-timesheets" />
          <QuickActionCard icon={CalendarDays} label="Active Shifts Today" count={qa.active_shifts_today || 0} color="cyan" onClick={() => setActiveTab('no-shows')} testId="qa-active" />
          <QuickActionCard icon={Users} label="Unconfirmed Bookings" count={qa.unconfirmed_bookings || 0} color="green" onClick={() => setActiveTab('timesheets')} testId="qa-unconfirmed" />
          {/* Iter-232c — Reimbursements visibility in the Operations Center */}
          <QuickActionCard icon={Receipt} label="Reimbursements" count={qa.pending_reimbursements || 0} color="purple" onClick={() => navigate('/admin/expenses')} testId="qa-reimbursements" />
        </div>

        {/* Tabbed Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-gray-50 border border-gray-200 p-1">
            <TabsTrigger value="no-shows" className="data-[state=active]:bg-red-600/20 data-[state=active]:text-red-500 text-gray-500" data-testid="tab-no-shows">
              <UserX className="w-4 h-4 mr-1.5" /> No-Shows
            </TabsTrigger>
            <TabsTrigger value="timesheets" className="data-[state=active]:bg-amber-600/20 data-[state=active]:text-amber-500 text-gray-500" data-testid="tab-timesheets">
              <ClipboardList className="w-4 h-4 mr-1.5" /> Timesheets
            </TabsTrigger>
            <TabsTrigger value="approved-payments" className="data-[state=active]:bg-emerald-600/20 data-[state=active]:text-emerald-600 text-gray-500" data-testid="tab-approved-payments">
              <DollarSign className="w-4 h-4 mr-1.5" /> Approved Payments
            </TabsTrigger>
            <TabsTrigger value="duplicate" className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400 text-gray-500" data-testid="tab-duplicate">
              <Copy className="w-4 h-4 mr-1.5" /> Duplicate Shift
            </TabsTrigger>
            <TabsTrigger value="overtime" className="data-[state=active]:bg-amber-600/20 data-[state=active]:text-amber-500 text-gray-500" data-testid="tab-overtime">
              <Timer className="w-4 h-4 mr-1.5" /> Overtime
            </TabsTrigger>
            {/* Iter-220q — Data Maintenance tab houses background system
                metrics (geocoding coverage, etc.) that were cluttering the
                main Admin Dashboard. */}
            <TabsTrigger value="data-maintenance" className="data-[state=active]:bg-cyan-600/20 data-[state=active]:text-cyan-600 text-gray-500" data-testid="tab-data-maintenance">
              <Database className="w-4 h-4 mr-1.5" /> Data Maintenance
            </TabsTrigger>
          </TabsList>

          <TabsContent value="no-shows">
            <Card className="bg-gray-850 border-gray-200 border">
              <CardHeader className="border-b border-gray-200 py-3 px-5">
                <CardTitle className="text-sm text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" /> Auto No-Show Detection
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5">
                <NoShowPanel />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="timesheets">
            <Card className="bg-gray-850 border-gray-200 border">
              <CardHeader className="border-b border-gray-200 py-3 px-5">
                <CardTitle className="text-sm text-gray-900 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-amber-500" /> Pending Timesheet Approvals
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5">
                <TimesheetsPanel />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="approved-payments">
            <Card className="bg-gray-850 border-gray-200 border">
              <CardHeader className="border-b border-gray-200 py-3 px-5">
                <CardTitle className="text-sm text-gray-900 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-600" /> Approved Timesheets &amp; Payments
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5">
                <AdminStaffPaymentsLifecycle embedded defaultStage="ready_for_payment" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="duplicate">
            <Card className="bg-gray-850 border-gray-200 border">
              <CardHeader className="border-b border-gray-200 py-3 px-5">
                <CardTitle className="text-sm text-gray-900 flex items-center gap-2">
                  <Copy className="w-4 h-4 text-indigo-400" /> Quick Shift Duplication
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5">
                <DuplicateShiftPanel />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="overtime">
            <Card className="bg-gray-850 border-gray-200 border">
              <CardHeader className="border-b border-gray-200 py-3 px-5">
                <CardTitle className="text-sm text-gray-900 flex items-center gap-2">
                  <Timer className="w-4 h-4 text-amber-500" /> Overtime Auto-Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5">
                <OvertimePanel />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Iter-220q — Data Maintenance tab: relocated from Admin Home.
              Surfaces background system metrics (currently geocoding coverage;
              future home for re-index, vacuum, healing jobs, etc.). */}
          <TabsContent value="data-maintenance">
            <Card className="bg-gray-850 border-gray-200 border" data-testid="data-maintenance-panel">
              <CardHeader className="border-b border-gray-200 py-3 px-5">
                <CardTitle className="text-sm text-gray-900 flex items-center gap-2">
                  <Database className="w-4 h-4 text-cyan-600" /> Data Maintenance
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> Talent Geocode Coverage
                  </h3>
                  <p className="text-[11px] text-gray-500 mb-3">
                    % of talent profiles with usable lat/lng for distance-based matching.
                    Run a backfill if coverage drops below 90% — the system also self-heals
                    in the background.
                  </p>
                  <GeocodeCoverageChip />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
